import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload as UseFormBasePayload } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'
import { findStatusByKey, statusColors } from '@functions/status'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as statusActions from '@redux/modules/status'
import type { AppDispatch } from '@redux/store'

import defaultFormState, { requiredFields } from '@models/status'
import type { StatusModel } from '@models/types'

const stringifyObjectKeys: (keyof StatusModel)[] = ['options']

export const generateUrls = () => ({
  statusesIndexUrl: '#/systemManager/statuses',
})

type UpdateStatusParams = {
  dispatch: AppDispatch,
  status: StatusModel,
  statusParams: Partial<StatusModel>,
}

const updateStatus = (params: UpdateStatusParams) => {
  const { status, statusParams, dispatch } = params

  const { updateStatus: updateFn } = statusActions

  const updatedParams = mergeAndStringifyEntityObjects(status, statusParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

type EditStatusParams = {
  status: StatusModel,
  showEditStatusModal: Function,
}

const editStatus = (params: EditStatusParams) => new Promise((resolve, reject) => {
  const { status, showEditStatusModal } = params

  if (showEditStatusModal){
    const payload = {
      status,
    }

    showEditStatusModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showEditStatusModal not defined in PageContext callbacks'))
})

export function useStatusForm(
  status: Partial<StatusModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormBasePayload {
  const { customRequiredFields = [], validateOn } = options

  const statusForm = useForm(
    defaultFormState,
    { entity: status, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [status.id, status.cache_key],
  )

  return statusForm
}

type UseStatusOptions = {
  defaultStatusKey?: string,
}

function useStatus(initEntity: Partial<StatusModel> = {}, options: UseStatusOptions = {}) {
  const { defaultStatusKey } = options
  // @ts-expect-error TS2345
  const { entity } = useLatestEntity(initEntity, 'statuses')

  const dispatch = useDispatch()

  const { loading, updating } = useSelector(reduxState => reduxState.statuses)
  const statuses = useSelector(reduxState => reduxState.entities.statuses)

  const status = entity.id || !defaultStatusKey ? entity : findStatusByKey(statuses, defaultStatusKey)

  const { callbacks } = useContext(PageContext)
  const { showEditStatusModal } = callbacks || {}

  // @ts-expect-error TS2339
  const { key } = status
  // @ts-expect-error TS7053
  const color = statusColors[key] || 'darkGrey'

  return {
    callbacks: {
      // @ts-expect-error TS2739
      editStatus: () => editStatus({ status, showEditStatusModal }),
      updateStatus: (statusParams: Partial<StatusModel>) => updateStatus(
        // @ts-expect-error TS2322
        { status, statusParams, dispatch },
      ),
    },
    color,
    loading,
    status,
    updating,
    urls: generateUrls(),
  }
}

export default useStatus
